import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import { CustomerInfo_customerInfo_edges_node } from "../../types/CustomerInfo";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "CustomerInfoList" }
);

interface CustomerInfoListProp extends ListProps {
  customers: CustomerInfo_customerInfo_edges_node[];
}

const CustomerInfoList: React.FC<CustomerInfoListProp> = ({
  customers,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onRowClick,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 5;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colNarrow}>
              <FormattedMessage defaultMessage="Customer ID" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Email" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Date Joined" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="First Name" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Last Name" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {customers &&
            renderCollection(
              customers,
              customer => (
                <TableRow
                  className={!!customer ? classes.tableRow : undefined}
                  hover={!!customer}
                  key={customer ? customer.id : "skeleton"}
                  onClick={
                    customer
                      ? onRowClick(btoa(`StoreOrder:${atob(customer.id)}`))
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => atob(customer.id),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(() => customer.email, <Skeleton />)}
                  </TableCell>
                  <TableCell>
                    <Moment format="YYYY-MM-DD HH:mm:ss" tz="Asia/Manila">
                      {customer?.date_joined ? (
                        customer?.date_joined
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => customer.first_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => customer.last_name,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No customers found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default CustomerInfoList;
